import { memo } from "react";
import { PiPlus, PiMinus, PiQuestion } from "react-icons/pi";
import { TiLocationArrowOutline } from "react-icons/ti";
import styled, { keyframes, css } from "styled-components";
import { showUploadTaskPanelAtom } from "../../stores/mapStore";
import { useAtom, useAtomValue } from 'jotai';
import { activeUploadTasksCountAtom, isTaskProcessingAtom } from "~/stores/UploadTaskStore";
import { Badge, Tooltip } from "antd";
import { CloudSyncOutlined, SyncOutlined } from "@ant-design/icons";

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(22, 119, 255, 0.45);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(22, 119, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(22, 119, 255, 0);
  }
`;

const BottomRightPanel = () => {
  const [showUploadTaskPanel, setShowUploadTaskPanel] = useAtom(showUploadTaskPanelAtom);
  const activeCount = useAtomValue(activeUploadTasksCountAtom);
  const isProcessing = useAtomValue(isTaskProcessingAtom);

  return (
    <UI_Wrapper>
      {/* Upload Tasks Progress Floating Button */}
      {isProcessing && (
        <UI_ProgressPill onClick={() => setShowUploadTaskPanel(true)}>
          <SyncOutlined spin style={{ color: '#1677ff', fontSize: 13, marginRight: 6 }} />
          <span>Đang tạo lát gạch ({activeCount})...</span>
        </UI_ProgressPill>
      )}

      <Tooltip
        title={
          isProcessing
            ? `Đang xử lý tạo lát gạch (${activeCount} tác vụ)... Bấm để xem tiến trình`
            : "Xem tiến trình tác vụ tải lên"
        }
        placement="topRight"
      >
        <UI_TaskItem
          $isProcessing={isProcessing}
          $isActive={showUploadTaskPanel}
          onClick={() => setShowUploadTaskPanel((prev) => !prev)}
        >
          <Badge
            count={isProcessing ? activeCount : 0}
            size="small"
            offset={[3, -3]}
          >
            {isProcessing ? (
              <SyncOutlined spin style={{ fontSize: 18, color: '#1677ff' }} />
            ) : (
              <CloudSyncOutlined style={{ fontSize: 18, color: showUploadTaskPanel ? '#1677ff' : '#262626' }} />
            )}
          </Badge>
        </UI_TaskItem>
      </Tooltip>
      {/* End Upload Tasks Progress Floating Button */}

      {/* Zoom to my location */}
      <UI_Item onClick={() => {
        if (navigator.geolocation && window.map) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              window.map?.flyTo({
                center: [pos.coords.longitude, pos.coords.latitude],
                zoom: 15,
                essential: true,
              });
            },
            (err) => {
              console.warn('Geolocation error:', err);
            }
          );
        }
      }}>
        <TiLocationArrowOutline size={18} />
      </UI_Item>
      {/* End Zoom to my location */}

      {/* Zoom Out */}
      <UI_Item onClick={() => {
        if (window.map) {
          window.map.setZoom(window.map.getZoom() - 1);
        }
      }}>
        <PiMinus size={18} />
      </UI_Item>
      {/* End Zoom Out */}

      {/* Zoom In */}
      <UI_Item onClick={() => {
        if (window.map) {
          window.map.setZoom(window.map.getZoom() + 1);
        }
      }}>
        <PiPlus size={18} />
      </UI_Item>
      {/* End Zoom In */}

      {/* Help */}
      <UI_Item onClick={() => {
        window.open('https://doc.geocloud.tlgeo.net/', '_blank');
      }}>
        <PiQuestion size={18} />
      </UI_Item>
      {/* End Help */}
    </UI_Wrapper>
  );
};

export default memo(BottomRightPanel);

const UI_Wrapper = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  z-index: 900;

  @media (max-width: 768px) {
    bottom: 20px;
    right: 12px;
    gap: 6px;
  }

  @media (max-width: 480px) {
    bottom: 16px;
    right: 8px;
    gap: 4px;
    flex-direction: column;
  }
`;

const UI_ProgressPill = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid #91caff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  font-weight: 500;
  color: #1677ff;
  cursor: pointer;
  white-space: nowrap;
  animation: ${pulseAnimation} 2s infinite;
  transition: all 0.2s ease;

  &:hover {
    background: #e6f4ff;
    border-color: #1677ff;
    transform: translateY(-1px);
  }
`;

const UI_Item = styled.div`
  display: flex;
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  color: #262626;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  &:hover {
    border: 1px solid #d9d9d9;
    backdrop-filter: blur(10px);
    background-color: #ffffff;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    font-size: 20px;
    background-color: rgba(255, 255, 255, 0.95);
  }
`;

const UI_TaskItem = styled(UI_Item)<{ $isProcessing?: boolean; $isActive?: boolean }>`
  position: relative;

  ${({ $isProcessing }) =>
    $isProcessing &&
    css`
      background-color: #e6f4ff;
      border-color: #91caff;
      animation: ${pulseAnimation} 2s infinite;
    `}

  ${({ $isActive, $isProcessing }) =>
    $isActive &&
    !$isProcessing &&
    css`
      background-color: #f0f5ff;
      border-color: #1677ff;
    `}
`;